import { useI18n } from "../../../i18n";
import { useAuth } from "../../../context/AuthContext";
import { useCustomer } from "../hooks";
import { InsightsPanel } from "../insights/InsightsPanel";
import { SkeletonBlock } from "../../../components/common/EmptyState";
import { EmailPrefs } from "../../../components/common/EmailPrefs";
import { RoleBadge } from "../../../components/common/RoleBadge";
import { TeamPanel } from "../account/TeamPanel";
import { Link } from "react-router-dom";
import { CreditCard } from "lucide-react";
import { useApi } from "../hooks";

const PayCard = () => {
  const { t } = useI18n();
  const s = useApi("paySummary", "/payments/summary", undefined, { staleTime: 0 });
  if (!s.data?.enabled) return null;
  return (
    <Link to="/app/pay" className="flex items-center gap-3 rounded-2xl bg-navy p-4 text-white shadow-sm transition-transform active:scale-[0.99]" data-testid="account-pay-card">
      <CreditCard className="h-6 w-6 text-brand" />
      <span className="min-w-0 flex-1"><span className="block font-semibold">{t("x.pay.accountCard")}</span><span className="block text-xs text-white/70">{t("x.pay.accountSub")}</span></span>
      <span className="rounded-full bg-brand px-4 py-2 text-sm font-semibold text-navy">{t("x.pay.cta")}</span>
    </Link>
  );
};

const Details = ({ c, financeLive, roles = [] }) => {
  const { t } = useI18n();
  const rows = [[t("common.code"), c.code], [t("account.vatNo"), c.vat_number || "—"], [t("account.address"), [c.address, c.area].filter(Boolean).join(", ")],
    financeLive && [t("account.salesRep"), c.sales_rep], financeLive && [t("account.terms"), t("account.termsDays", { n: c.payment_terms_days })]].filter(Boolean);
  return (
    <section className="rounded-2xl bg-white p-4 shadow-sm" data-testid="customer-details">
      <p className="text-lg font-bold text-navy" data-testid="account-legal-name">{c.legal_name || c.name}</p>
      {c.legal_name && c.legal_name !== c.name && <p className="text-sm text-slate-600">{c.name}</p>}
      <div className="mt-2 flex flex-wrap gap-1" data-testid="account-roles">{roles.map((r) => <RoleBadge key={r} role={r} />)}</div>
      <dl className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
        {rows.map(([k, v]) => <div key={k}><dt className="text-xs text-slate-500">{k}</dt><dd className="font-medium text-navy">{v}</dd></div>)}
      </dl>
    </section>
  );
};

// Balances, payments and statements are hidden until the live SoftOne connection
export default function Account() {
  const { t } = useI18n();
  const { user } = useAuth();
  const customer = useCustomer(user.customer_code);
  return (
    <div className="space-y-5" data-testid="account-page">
      <h1 className="text-2xl font-bold text-navy sm:text-3xl">{t("account.title")}</h1>
      {customer.isLoading ? <SkeletonBlock className="h-28" /> : customer.data && <Details c={customer.data} financeLive={user.finance_live} roles={user.roles} />}
      {user.permissions.includes("payments:pay") && <PayCard />}
      {user.finance_live && (user.permissions.includes("insights:amounts") || user.permissions.includes("orders:company")) ? <>
        <h2 className="text-base font-semibold text-navy md:text-lg">{t("insights.tab")}</h2>
        <InsightsPanel />
      </> : null}
      <EmailPrefs cats={user.permissions.includes("orders:approve") ? ["orders", "approvals", "messages"] : ["orders", "messages"]} />
      {user.permissions.includes("team:manage") && <TeamPanel me={user.email} />}
    </div>
  );
}
